import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  WhatsappLogo,
  MicrosoftTeamsLogo,
  ChatCircleText,
  Copy,
  Check,
  ShareNetwork,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ShareDialog({ open, onOpenChange, deckName }) {
  const [copied, setCopied] = useState(false);

  const url = typeof window !== "undefined" ? window.location.origin : "";
  const message = deckName
    ? `Estoy usando LaunchPad para organizar mis apps y links favoritos. Mira mi panel "${deckName}" 👉 ${url}`
    : `Estoy usando LaunchPad para organizar mis apps y links favoritos 👉 ${url}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      handleCopy();
      return;
    }
    try {
      await navigator.share({
        title: "LaunchPad",
        text: message,
        url,
      });
    } catch (e) {
      // user cancelled
    }
  };

  const channels = [
    {
      id: "sms",
      label: "Mensaje",
      Icon: ChatCircleText,
      color: "#2563EB",
      iconColor: "text-white",
      href: `sms:?&body=${encodeURIComponent(message)}`,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      Icon: WhatsappLogo,
      color: "#25D366",
      iconColor: "text-white",
      href: `https://wa.me/?text=${encodeURIComponent(message)}`,
    },
    {
      id: "teams",
      label: "Teams",
      Icon: MicrosoftTeamsLogo,
      color: "#4B53BC",
      iconColor: "text-white",
      href: `https://teams.microsoft.com/share?href=${encodeURIComponent(url)}&msgText=${encodeURIComponent(message)}&preview=true`,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="share-dialog"
        className="bg-deck-sidebar border-white/10 text-white max-w-md"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-11 h-11 rounded-2xl bg-[#2563EB] flex items-center justify-center deck-tile shrink-0">
              <ShareNetwork size={22} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#FACC15]">
                COMPARTIR
              </div>
              <DialogTitle className="text-white font-heading text-2xl">
                Comparte LaunchPad
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 mt-2">
          {channels.map((c) => (
            <a
              key={c.id}
              data-testid={`share-${c.id}`}
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition deck-tile"
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: c.color }}
              >
                <c.Icon size={28} weight="fill" className={c.iconColor} />
              </div>
              <span className="text-[10px] font-mono uppercase tracking-[0.1em] text-white/70">
                {c.label}
              </span>
            </a>
          ))}
        </div>

        <div className="mt-4 space-y-2">
          <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
            Link directo
          </label>
          <div className="flex gap-2">
            <Input
              data-testid="share-link-input"
              readOnly
              value={url}
              className="bg-white/5 border-white/10 text-white font-mono text-xs flex-1"
              onFocus={(e) => e.target.select()}
            />
            <Button
              data-testid="share-copy-btn"
              onClick={handleCopy}
              className="bg-[#2563EB] text-white hover:bg-[#1D4ED8] font-heading font-bold whitespace-nowrap"
            >
              {copied ? (
                <>
                  <Check size={14} className="mr-1" weight="bold" /> Listo
                </>
              ) : (
                <>
                  <Copy size={14} className="mr-1" weight="bold" /> Copiar
                </>
              )}
            </Button>
          </div>
        </div>

        {typeof navigator !== "undefined" && navigator.share && (
          <Button
            data-testid="share-native-btn"
            onClick={handleNativeShare}
            variant="outline"
            className="mt-3 w-full bg-transparent border-white/15 text-white hover:bg-white/5"
          >
            <ShareNetwork size={16} weight="bold" className="mr-2" />
            Compartir...
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
